/**
 * Remote access — the SPA's side of the credential (remote-access program
 * half 1, 2026-09-07; tasks/plan-remote-access.md D4–D5).
 *
 * On the machine AgntSpace runs on nothing here ever fires: the server says
 * `required: false` for a loopback caller. From another machine the browser
 * holds an HttpOnly session cookie the server set at sign-in, so none of the
 * ~400 `fetch("/api/…")` sites and none of the WebSocket clients need to
 * know — they carry it automatically. The ONE thing the SPA must do is learn
 * that it needs to sign in: at boot from `/api/access/session`, and mid-use
 * when a fetch answers 401 (the interceptor below) or a socket closes 4401.
 */

export const AUTH_REQUIRED_EVENT = "agntspace:auth-required";
export const WS_CLOSE_AUTH_REQUIRED = 4401;
/** The node serves TLS itself and refused a plain-HTTP credential (half 2, D8.3). */
export const WS_CLOSE_TLS_REQUIRED = 4426;

export interface AccessSession {
	required: boolean;
	authenticated: boolean;
	via: string;
	scope: string;
	expires_at?: string | null;
	peer?: string;
	/** Half 2: the node serves HTTPS itself under configured names. */
	tls_required?: boolean;
	/** Where to go instead when reached over plain http (empty when not serving TLS). */
	https_url?: string;
	scheme?: string;
	/** True when THIS request would be refused for being plain http from another machine. */
	plain_http_refused?: boolean;
	/** N3: the name of the token that admitted this client ("Wolf's phone"); "" on loopback. */
	device?: string;
}

export interface TlsCertificateInfo {
	issued: boolean;
	names: string[];
	all_names: string[];
	not_valid_after: string | null;
	cert_path: string;
	ca_path: string;
	ca_fingerprint_sha256: string;
}

export interface TlsStatus {
	enabled: boolean;
	names: string[];
	https_url: string;
	port: number;
	certificate: TlsCertificateInfo;
	listener: { running: boolean; host: string; port: number; certificate: string } | null;
	ca_download_path: string;
	restarted?: boolean;
}

async function detailOf(res: Response, fallback: string): Promise<string> {
	try {
		const body = await res.json();
		const d = body?.detail;
		if (typeof d === "string") return d;
		if (d && typeof d === "object") return String(d.message ?? d.error ?? JSON.stringify(d));
	} catch { /* no body */ }
	return `${fallback} (${res.status})`;
}

export async function fetchTlsStatus(): Promise<TlsStatus> {
	const res = await fetch("/api/access/tls", { credentials: "same-origin" });
	if (!res.ok) throw new Error(await detailOf(res, "Could not read the TLS status"));
	return (await res.json()) as TlsStatus;
}

/** PUT the names this node is reached by; empty = loopback only. The server re-issues the certificate and restarts its listener. */
export async function setTlsNames(names: string[]): Promise<TlsStatus> {
	const res = await fetch("/api/access/tls", { method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ names }) });
	if (!res.ok) throw new Error(await detailOf(res, "Could not save the certificate names"));
	return (await res.json()) as TlsStatus;
}

export async function fetchAccessSession(): Promise<AccessSession> {
	try {
		const res = await fetch("/api/access/session", { credentials: "same-origin" });
		if (!res.ok) return { required: false, authenticated: true, via: "unknown", scope: "" };
		return (await res.json()) as AccessSession;
	} catch {
		// An unreachable server is the app's own problem to show; never a login wall.
		return { required: false, authenticated: true, via: "unknown", scope: "" };
	}
}

export async function loginWithToken(token: string): Promise<{ ok: boolean; scope: string; expires_at: string }> {
	const res = await fetch("/api/access/login", {
		method: "POST",
		credentials: "same-origin",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ token: token.trim() }),
	});
	if (!res.ok) {
		let detail = "";
		try {
			const body = await res.json();
			detail = typeof body?.detail === "string" ? body.detail : String(body?.detail?.message ?? "");
		} catch { /* no body */ }
		throw new Error(detail || `Sign-in failed (${res.status})`);
	}
	return (await res.json()) as { ok: boolean; scope: string; expires_at: string };
}

export async function logoutAccess(): Promise<void> {
	await fetch("/api/access/logout", { method: "POST", credentials: "same-origin" }).catch(() => undefined);
}

export function noteAuthRequired(): void {
	window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
}

/** A WebSocket the server closed with 4401 is the same signal as a 401. */
export function noteSocketClose(code: number | undefined): void {
	if (code === WS_CLOSE_AUTH_REQUIRED) noteAuthRequired();
}

const EXEMPT = ["/api/access/login", "/api/access/session", "/api/access/logout", "/api/access/ca.crt", "/api/access/pair"];
let installed = false;

/**
 * Wrap `window.fetch` ONCE: a same-origin `/api/…` answer of 401 means the
 * session is gone (expired, revoked) — announce it so the login page shows,
 * and return the response unchanged so every caller keeps its own handling.
 */
export function installAuthInterceptor(target: Window & typeof globalThis = window): void {
	if (installed || typeof target.fetch !== "function") return;
	installed = true;
	const original = target.fetch.bind(target);
	target.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
		const res = await original(input, init);
		try {
			const raw = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
			const url = new URL(raw, target.location.href);
			if (res.status === 401 && url.origin === target.location.origin && url.pathname.startsWith("/api/") && !EXEMPT.includes(url.pathname)) {
				noteAuthRequired();
			}
		} catch { /* not a URL we can judge */ }
		return res;
	}) as typeof fetch;
}

/** Test seam: forget the wrapper so a fresh install can be asserted. */
export function _resetAuthInterceptorForTests(): void {
	installed = false;
}

// ── device pairing (remote-node program N1, 2026-09-08) ──────────────────────
export interface DevicePairing {
	id: string;
	scope: string;
	label: string;
	created_by: string;
	created_at: string;
	expires_at: string;
}

export interface CreatedPairing extends DevicePairing {
	/** Shown ONCE. */
	code: string;
	pair_url: string;
	address: string;
	address_guessed: boolean;
	ca_fingerprint_sha256: string;
	ca_download_path: string;
	note: string;
}

export async function createPairing(label: string, scope: "full" | "read_only"): Promise<CreatedPairing> {
	const res = await fetch("/api/access/pairings", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ label, scope }) });
	if (!res.ok) throw new Error(await detailOf(res, "Could not create a pairing code"));
	return (await res.json()) as CreatedPairing;
}

export async function listPairings(): Promise<DevicePairing[]> {
	const res = await fetch("/api/access/pairings", { credentials: "same-origin" });
	if (!res.ok) throw new Error(await detailOf(res, "Could not list pairings"));
	return ((await res.json()) as { pairings: DevicePairing[] }).pairings ?? [];
}

export async function cancelPairing(id: string): Promise<void> {
	const res = await fetch(`/api/access/pairings/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "same-origin" });
	if (!res.ok) throw new Error(await detailOf(res, "Could not cancel the pairing"));
}

/** The device side: redeem a code. A browser is signed in by the cookie the server sets. */
export async function pairDevice(code: string, deviceLabel: string): Promise<{ ok: boolean; scope: string; device_label: string }> {
	const res = await fetch("/api/access/pair", {
		method: "POST",
		credentials: "same-origin",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ code: code.trim(), device_label: deviceLabel, device_kind: "browser" }),
	});
	if (!res.ok) throw new Error(await detailOf(res, "Pairing failed"));
	return (await res.json()) as { ok: boolean; scope: string; device_label: string };
}
